import spartaEventsUtility from "utilities/global/sparta-events-utility/2.4.1/js/sparta-events-utility";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.1.1/js/sparta-store-utility";
import spartaTransportUtility from "utilities/global/sparta-transport-utility/4.2.0/js/sparta-transport-utility";
/**
 * @name        Data Asset Loader
 * @file        data-asset-loader.js
 * @description Dynamically loading assets from a utility's data directory
 * @classdesc   Dynamically loading assets from a utility's data directory
 * @global      Creates a global DataAssetLoader object
 * @author      E Allen & J Black
 * @copyright   © 2017 Bank of America Corporation. All rights reserved.
 */

/* global spartaStoreUtility, spartaTransportUtility, spartaEventsUtility */

/**
 * @const utilityNamespace
 * @description namespace for Data Asset Loader.
 */

 const utilityNamespace = 'DataAssetLoader';
 const eventsUtil = 'spartaEventsUtility';

 /**
  * @const EVENTS
  * @description constant for storing namespaced event strings
  */
 
 const EVENTS = {
   ASSET_LOADED: `ASSET_LOADED.${utilityNamespace}`,
   ASSET_FAILED: `ASSET_FAILED.${utilityNamespace}`,
   APP_CONTEXT: `APP_CONTEXT_LOADED.${eventsUtil}`,
 };
 
 /**
  * @method getRequireVersion
  * @description Checks to see if the utility is running within a widget and gets
  * the correct require.
  * @return {object} requireObj
  */
 function getRequireVersion() {
   // initialize variable to store our requireObject
   let requireObj;
 
   // check to see if we are in a Sparta Widget or a Sparta Page
   const widgetContext = spartaStoreUtility.getData('applicationContext');
 
   // if this is a widget
   if (widgetContext) {
     // get the widget-specific require
     requireObj = window.sparta.require[widgetContext.name][widgetContext.version].require;
   } else if (window.require) {
     // otherwise just use require
     requireObj = window.require;
   }
 
   // return the relevant require Object
   return requireObj;
 }
 
 /**
  * @method ajaxComplete
  * @description emit the relevant spartaEventsUtility event after loading via AJAX
  * @param {Object} xhr the data returned by the Sparta Transport method
  * @param {String} filename name of the asset we loaded
  * @param {Object} events Event object
  * @returns {undefined}
  */
 function ajaxComplete(xhr, filename, events) {
   // generate event data Object to pass to spartaEventsUtility subscribers
   const evtData = {
     filename,
     xhr,
   };
 
   // if the XHR succeeded or we just got back a JSON Object
   // NOTE: Transport doesn't preserve the XHR data on success
   if ((xhr.status >= 200 && xhr.status < 400) || (!xhr.status && !xhr.DONE)) {
     // publish the success event
     spartaEventsUtility.pub(events.success, evtData);
   } else {
     // publish the failure event
     spartaEventsUtility.pub(events.failure, evtData);
   }
 }
 
 /**
  * @method requireComplete
  * @description emit the relevant spartaEventsUtility event after loading via require
  * @param {any} filename name of the asset we loaded
  * @param {Object} events Event object
  * @returns {undefined}
  */
 function requireComplete(filename, events) {
   // publish the asset loaded event
   spartaEventsUtility.pub(events.require, { filename });
 }
 
 /**
  * @class DataAssetLoader
  * @description Class to make it easy to work with dynamically loading assets for
  * a Sparta Component
  */
 class DataAssetLoader {
   /**
    * @method constructor
    * @description Creates an instance of DataAssetLoader.
    * @param {String} utilityString the name of the Sparta Component in `site/component-name` form
    * @param {string} [loadPath='data'] the path in the component's directory to load assets from
    * @memberof DataAssetLoader
    */
   constructor(utilityString, loadPath = 'data') {
     // if we were given a utility string
     if (utilityString) {
       // initialize class property for triggering namespaced events after loading an asset
       this.callerNamespace = utilityString;
 
       try {
         // get the correct require for the current Sparta Context
         this.require = getRequireVersion();
 
         if (this.require) {
           // get the asset path from our Sparta Context's require
           this.config = this.getImporterConfig(utilityString, loadPath);
         } else { // assistance for v5 widgets
           spartaEventsUtility.sub(EVENTS.APP_CONTEXT, () => {
             this.require = getRequireVersion();
             this.config = this.getImporterConfig(utilityString, loadPath);
           });
         }        
       } catch (e) {
         console.warn(`DATA ASSET LOADER: ${e}`);
       }
     }
   }
 
   /**
    * @property {Object} EVENTS exposes event name constant
    * @description getter for EVENTS constant
    * @returns {Object} Events object
    */
   get EVENTS() {
     return EVENTS;
   }
 
   /**
    * @method getImporterConfig
    * @description Retreives the appropriate URL path to the utility provided
    * @param {String} utilityString string for component name; passed from constructor
    * @param {String} loadPath string for path in component directory; passed from constructor
    * @memberof DataAssetLoader
    * @returns {Object} Configuration object
    */
   getImporterConfig(utilityString, loadPath) {
     // RegEx to match the base path with utility version number
     const basePathRegex = new RegExp(`.*?/${utilityString}/(\\d+.\\d+.\\d+-[a-zA-Z]+|\\d+.\\d+.\\d+)`, 'g');
 
     // get path for this component from require config
     const basePathResult = this.require.toUrl(utilityString).match(basePathRegex)[0];
 
     // return configuration Object with utilityString, require path, and loadPath
     return {
       utilityString,
       basePathResult,
       loadPath,
     };
   }
 
   /**
    * @method getValidationRules
    * @description Retrieves JSON files associated with a rule from the data folder.
    * @param {String} filename the name of the file to load from our loadPath
    * @param {String} [type='json'] the type of asset we are loading
    * @memberof DataAssetLoader
    * @returns {Promise|boolean} Promise contains a list of events. False if not filename or config.
    */
   loadAsset(filename, type = 'json') {
     // if we have a filename and a configuration
     if (filename && this.config) {
       // generate event names
       const success = `${this.EVENTS.ASSET_LOADED}.${this.callerNamespace}.${filename}`;
       const failure = `${this.EVENTS.ASSET_FAILED}.${this.callerNamespace}.${filename}`;
 
       // create Object for returning relevant event names
       const jsonEvents = {
         success,
         failure,
       };
 
       // NOTE: because require doesn't give us a success or failure, we only have one event
       const requireEvents = {
         require: success,
       };
 
       // respond to the type with the relevant loading method
       switch (type) {
         case 'js':
           this.loadScript(filename, requireEvents);
 
           break;
         case 'css':
           this.loadStylesheet(filename, requireEvents);
 
           break;
         case 'json':
         default:
           this.loadViaAjax(filename, jsonEvents);
       }
 
       // if this isn't a require load, return the JSON event Object for the component
       // to subscribe to
       if (type === 'json') {
         return jsonEvents;
       }
 
       // if this is a require load, return the require event Object for the component
       // to subscribe to
       return requireEvents;
     }
 
     // return false so that component knows not to listen for anything
     return false;
   }
 
   /**
    * @method loadScript
    * @description loads a JavaScript asset via require
    * @param {String} filename name of the asset to load
    * @param {Object} evts Event object
    * @memberof DataAssetLoader
    * @returns {*} Require status
    */
   loadScript(filename, evts) {
     // if we were given a filename
     if (filename) {
       // load it with require
       return this.require([`${this.generateAssetURL(filename)}`], requireComplete(filename, evts));
     }
 
     return false;
   }
 
   /**
    * @method loadStylesheet
    * @description loads a CSS asset via require-css
    * @param {String} filename name of the asset to load
    * @param {Object} evts Event object
    * @memberof DataAssetLoader
    * @returns {*} Require status
    */
   loadStylesheet(filename, evts) {
     // if we were given a filename
     if (filename) {
       // load it with require-css
       return this.require([`css!${this.generateAssetURL(filename)}`], requireComplete(filename, evts));
     }
 
     return false;
   }
 
   /**
    * @method loadViaAjax
    * @description loads a JSON asset via spartaTransportUtility
    * @param {String} filename name of the asset to load
    * @param {Object} evts Event object
    * @memberof DataAssetLoader
    * @returns {*} Return the SpartaTransportUtility subscription or false.
    */
   loadViaAjax(filename, evts) {
     // if we were given a filename
     if (filename) {
       // create the XHR Object for spartaTransportUtility
       const subscription = {
         cache: true,
         dataType: 'json',
         method: 'GET',
         error(xhr) {
           ajaxComplete(xhr, filename, evts);
         },
         success(data) {
           ajaxComplete(data, filename, evts);
         },
         url: this.generateAssetURLWithQueryString(filename),
       };
 
       // return the SpartaTransportUtility subscription
       return spartaTransportUtility.subscribe(subscription);
     }
 
     return false;
   }
 
   /**
    * @method generateAssetURL
    * @description create the URL where the given asset can be found
    * @param {String} filename name of the asset to load
    * @memberof DataAssetLoader
    * @returns {String} Asset URL
    */
   generateAssetURL(filename) {
     // if we have a config
     if (this.config) {
       // get URL path and loadPath from configuration
       const { basePathResult, loadPath } = this.config;
       // return the relevant URL
       return `${basePathResult}/${loadPath}/${filename}`;
     }
 
     return false;
   }
 
   /**
 * @method generateAssetURLWithQueryString
 * @description create the URL with query param where the given asset can be found
 * @param {String} filename name of the asset to load
 * @memberof DataAssetLoader
 * @returns {String} Asset URL with query param
 */
   generateAssetURLWithQueryString(filename) {
     const url = window.location;
     // if we have a config
     if (this.config) {
       // get URL path and loadPath from configuration
       const { basePathResult, loadPath } = this.config;
       const assetURL = `${basePathResult}/${loadPath}/${filename}`;
       // return the asset URL
       return `${(url.host.includes('.bankofamerica.com') ? `${assetURL}?ro=boa` : url.host.includes('.ml.com') ? `${assetURL}?ro=ml` : `${assetURL}`)}`;
     }
     return false;
   }
 }
 
 // export the dataAssetLoader for use in modules and utilities
 export default DataAssetLoader;
 